// ─────────────────────────────────────────────────────────────────────────────
// REQ-081 / C2 — «Red de Reconocimiento — Flujos Cross-OU», dentro del panel Demográfico.
//
// 🔴 Esto ERA un grafo SVG dibujado a mano (nodos = OUs, aristas = flujos). La maqueta
// (`rep-panel-demografico`) no pinta un grafo: pinta una **tabla de tres columnas**
// —`De → A | Recos | Intensidad`— y una **tarjeta con el % de colaboración cross-OU**. El dato es el
// mismo y la fuente no cambia (`OnaGraphDto`); lo que cambia es la forma, y la maqueta manda.
//
// No se pierde ninguna función: el grafo enseñaba volumen por OU y qué flujos cruzaban, y las dos
// cosas siguen ahí —el volumen en la columna Recos ordenada, y el cruce marcado por fila y resumido
// en la tarjeta—. Lo que se gana es poder LEER los números, que en un grafo de 15 OUs no se podía.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecognitionNetwork, useRecognitionCatalog, getFundamentalName, getFundamentalColor } from '@/api/recognition';
import type { RecognitionReportParams } from '@/api/recognition';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import {
  ReportStatus, KpiRow, SuppressedNote, filtrosActivosDe, soloPersonaDe,
} from '../reportsShared';
import { Paginacion, usePaginacion } from '../Paginacion';

/** Fila de la tabla: un flujo de una OU a otra. */
interface Flujo {
  key: string;
  de: string;
  a: string;
  recos: number;
  /** 0-100, relativo al flujo más grande. Ya NO se pinta en la tabla (C2/C4, 2026-08-13): la
   *  columna «Intensidad» la reemplazó el Fundamental dominante. Se conserva calculada porque las
   *  pruebas la afirman y el día que vuelva a necesitarse el cálculo ya está hecho. */
  intensidad: number;
  crossOu: boolean;
  /**
   * C2/C4 (2026-08-13) — id del Fundamental más reconocido en este flujo (`OnaEdgeDto.
   * fundamentalDominante`). Reemplaza la columna «Intensidad» 0-100%, descartada por la spec.
   * `null` sin datos suficientes.
   */
  fundamentalDominante: string | null;
}

/**
 * Construye la tabla y el % de colaboración cruzada.
 *
 * Exportada para poder probarla sin montar la pantalla: es la única parte con reglas —qué es
 * intensidad, cómo se calcula el %— y probarla a través del render la dejaría a merced del contexto
 * de OUs.
 */
export function construirFlujos(
  aristas: ReadonlyArray<{
    sourceOuId: string;
    targetOuId: string;
    peso: number;
    crossOu: boolean;
    /** Opcional para no romper llamadores previos a C2/C4; ausente = `null` (sin Fundamental dominante). */
    fundamentalDominante?: string | null;
  }>,
  nombreDeOu: (id: string) => string,
) {
  // El máximo se calcula sobre TODOS los flujos, no sobre los cross-OU: la intensidad compara cada
  // flujo con el mayor del reporte, y acotarla al subconjunto haría que el mayor de los cruzados
  // pareciera el mayor de todos.
  const maxPeso = Math.max(1, ...aristas.map((a) => a.peso));
  const total = aristas.reduce((suma, a) => suma + a.peso, 0);
  const cruzados = aristas.reduce((suma, a) => suma + (a.crossOu ? a.peso : 0), 0);

  const flujos: Flujo[] = aristas
    .map((a, i) => ({
      key: `${a.sourceOuId}-${a.targetOuId}-${i}`,
      de: nombreDeOu(a.sourceOuId),
      a: nombreDeOu(a.targetOuId),
      recos: a.peso,
      intensidad: Math.round((a.peso / maxPeso) * 100),
      crossOu: a.crossOu,
      fundamentalDominante: a.fundamentalDominante ?? null,
    }))
    .sort((x, y) => y.recos - x.recos);

  return {
    flujos,
    // Se calcula pero NO se pinta como tarjeta: RF-153 prohíbe la métrica agregada de cruzados (ver
    // la nota del render). Se conserva expuesto porque la prueba lo afirma —pesa por
    // RECONOCIMIENTOS y no por número de flujos— y porque el día que el RF cambie, el cálculo ya
    // está probado. `null` sin flujos: un 0 % afirmaría «nadie colabora fuera de su unidad», y sin
    // datos no se puede afirmar eso.
    pctCrossOu: total > 0 ? Math.round((cruzados / total) * 100) : null,
  };
}

export function NetworkTab({ params }: Readonly<{ params: RecognitionReportParams }>) {
  const { t, i18n } = useTranslation('recognition');
  const q = useRecognitionNetwork(params);
  const graph = q.data;
  const isEmpty = !!graph && graph.nodos.length === 0;
  const { resolveOuName } = useOuNameResolver();
  // Los nodos de esta red son OUs, no personas: la persona no acota el grafo, solo avisa.
  const soloPersona = useMemo(() => soloPersonaDe(filtrosActivosDe(params)), [params]);

  // Solo se consume `flujos`: el % de cruzados se calcula y se PRUEBA, pero no se pinta (RF-153).
  const { flujos } = useMemo(
    // AnalyticsService todavía no resuelve `ouName`: se ignora el valor de la API y se resuelve en
    // el front, igual que el mapa de calor y el resto de AnalyticsView.
    () => construirFlujos(graph?.aristas ?? [], resolveOuName),
    [graph, resolveOuName],
  );

  // C2/C4 — «Fundamental dominante» reemplaza la columna «Intensidad». `fundamentalDominante` es un
  // id de Fundamental (viaja crudo desde AnalyticsService, igual que `ouId`): se resuelve aquí con
  // el MISMO catálogo y la MISMA paleta de color que el resto de la plataforma (`getFundamentalName`/
  // `getFundamentalColor`) — no se inventa una paleta nueva.
  const { data: catalog } = useRecognitionCatalog();
  const lang = i18n.language.split('-')[0];
  const fundamentalById = useMemo(
    () => new Map((catalog ?? []).map((f, i) => [
      f.id, { name: getFundamentalName(f, lang), color: getFundamentalColor(f, i) },
    ])),
    [catalog, lang],
  );

  const { visibles, pagina, totalPaginas, total, tamano, setPagina, cambiarTamano } = usePaginacion(flujos);

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-share-nodes" aria-hidden="true" /> {t('reports.network.title')}
          </div>
          <div className="au-card-sub">{t('reports.network.subtitle')}</div>
        </div>
      </div>

      <AvisoFiltroNoAplica filtros={soloPersona} motivo="redOu" />

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        {graph && (
          <>
            {graph.suprimido && <SuppressedNote />}

            {/*
              * 🔴 La maqueta pinta aquí una tarjeta «% Cross-OU» y NO se añade, a propósito.
              *
              * **RF-153 la prohíbe explícitamente.** Javier la quitó el 2026-08-05 (`108124e`) y dejó
              * una prueba para que no reapareciera «por accidente en un cambio futuro» — y este
              * cambio estuvo a punto de ser ese accidente. Su commit acota el alcance del RF: *«pide
              * quitar la métrica de KPI, no el resalte visual del grafo»*, así que la marca de cruce
              * POR FILA de la tabla de abajo sí se conserva; el agregado en tarjeta, no.
              *
              * Es el mismo caso que la columna «Puntos» de Ganadores: cuando un requisito explícito
              * contradice a la maqueta, gana el requisito y se deja escrito por qué.
              */}
            <KpiRow
              items={[
                { label: t('reports.network.ous'), value: graph.nodos.length, color: '#342F82' },
                { label: t('reports.network.flows'), value: graph.aristas.length, color: '#40B8E9' },
              ]}
            />

            {flujos.length > 0 && (
              <div className="rr-table-wrap rr-table-wrap--top-gap">
                <table className="rr-table">
                  <thead>
                    <tr>
                      <th>{t('reports.network.colFlow')}</th>
                      <th className="rr-num">{t('reports.network.colRecognitions')}</th>
                      <th title={t('reports.network.colDominantFundamentalHelp')}>
                        {t('reports.network.colDominantFundamental')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((f) => {
                      const fundamental = f.fundamentalDominante
                        ? fundamentalById.get(f.fundamentalDominante)
                        : undefined;
                      return (
                        <tr key={f.key}>
                          <td>
                            {f.de} → {f.a}
                            {/* El cruce se marca por fila, que es lo que el grafo señalaba con el
                                trazo discontinuo. Sin esto, la tabla perdería esa distinción. */}
                            {f.crossOu && (
                              <span className="rr-rank-tag"> · {t('reports.network.legendCross')}</span>
                            )}
                          </td>
                          <td className="rr-num">{f.recos}</td>
                          {/* C2/C4 — sigue siendo el Fundamental dominante del flujo, no el % de
                              Intensidad que la spec descartó (RF-153): lo que cambia es la forma —
                              una barra coloreada con SU color y largo proporcional a `intensidad`
                              (0-100, ya calculada, antes sin pintar) en vez del chip con punto+nombre.
                              El nombre viaja accesible (`rr-sr-only` + `title`) aunque la barra no lo
                              imprima en pantalla. `fundamentalDominante` puede venir con un id fuera
                              del catálogo cargado (carrera de caché) — se degrada al id crudo, nunca a
                              una barra en blanco sin explicación. */}
                          <td className="rr-fund-cell">
                            {fundamental ? (
                              <div className="rr-fund-bar-wrap" title={fundamental.name}>
                                <span className="rr-sr-only">{fundamental.name}</span>
                                <div
                                  className="rr-fund-bar"
                                  style={{ width: `${f.intensidad}%`, background: fundamental.color }}
                                />
                              </div>
                            ) : (f.fundamentalDominante ?? '—')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Paginacion
                  pagina={pagina}
                  totalPaginas={totalPaginas}
                  total={total}
                  tamano={tamano}
                  onPagina={setPagina}
                  onTamano={cambiarTamano}
                  siempreVisible
                />
              </div>
            )}
          </>
        )}
      </ReportStatus>
    </div>
  );
}
